/**
 * ChatLayout
 * Shared layout shell with sidebar and content area.
 * Used by all chat routes (AI, DM, Ritual).
 * Sidebar is hidden when no conversations exist (shows full-width landing page).
 * When collapsed, shows a narrow avatar rail per Figma design.
 */

import { PanelLeftOpen } from "lucide-react"
import { useState } from "react"
import { Link, Outlet, useLocation } from "react-router-dom"
import aiAvatarImg from "@/assets/icons/ai-conversation-avatar.png"
import { Avatar } from "@/components/ui/avatar"
import { useIsMobile } from "@/hooks/useIsMobile"
import { useTranslation } from "@/language"
import { cn } from "@/lib/utils"
import { useAllConversations } from "../hooks/useUnifiedConversations"
import type { UnifiedConversation } from "../types/unified-chat.types"
import { getConversationRoute } from "../utils/conversationRoutes"
import { ChatSidebar } from "./chat/ChatSidebar"

function CollapsedAvatarRail({
  conversations,
  onExpand,
}: {
  conversations: UnifiedConversation[]
  onExpand: () => void
}) {
  const { t } = useTranslation("chats")
  const location = useLocation()

  return (
    <div className="w-[64px] shrink-0 bg-white/5 border-r border-t border-b border-border flex flex-col min-h-0">
      {/* Collapse/expand icon */}
      <div className="h-[64px] w-[64px] flex items-center justify-center border-b border-border shrink-0">
        <button
          type="button"
          onClick={onExpand}
          aria-label={t("common.expandSidebar")}
          className="relative z-10 text-foreground hover:opacity-70 transition-opacity p-2 -m-2"
          title={t("common.expandSidebar")}
        >
          <PanelLeftOpen className="size-5" />
        </button>
      </div>

      {/* Avatar list */}
      <div className="flex-1 overflow-y-auto">
        {conversations.map(conv => {
          const route = getConversationRoute(conv)
          const isActive = location.pathname === route

          return (
            <Link
              key={`${conv.type}-${conv.id}`}
              to={route}
              className={cn(
                "h-[60px] w-[64px] flex items-center justify-center shrink-0 border-b border-border transition-colors",
                isActive ? "bg-brand-slate/75" : "bg-transparent hover:bg-white/5",
              )}
              title={conv.name}
            >
              {conv.type === "ai" ? (
                <img src={aiAvatarImg} alt={conv.name} className="size-[30px] rounded-full" />
              ) : conv.type === "ritual-group" ? (
                <Avatar
                  src={conv.profilePhoto || undefined}
                  alt={conv.name}
                  fallback={conv.name || "?"}
                  size="sm"
                  className="rounded-[5px]"
                />
              ) : (
                <Avatar
                  src={conv.profilePhoto || undefined}
                  alt={conv.name}
                  fallback={conv.name || "?"}
                  size="sm"
                />
              )}
            </Link>
          )
        })}
      </div>
    </div>
  )
}

export function ChatLayout() {
  const [collapsed, setCollapsed] = useState(false)
  const location = useLocation()
  const isMobile = useIsMobile()
  const { data, isSuccess, isLoading } = useAllConversations()

  const hasConversations = (data?.conversations?.length ?? 0) > 0
  const conversations = data?.conversations ?? []
  const isChatThread = location.pathname !== "/chats"

  const toggle = () => setCollapsed(prev => !prev)

  if (isLoading || !isSuccess) {
    return (
      <div className="flex h-[calc(100vh-48px-85px)] md:h-[calc(100vh-70px)] overflow-hidden items-center justify-center">
        <div className="size-6 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    )
  }

  if (!hasConversations) {
    return (
      <div className="flex h-[calc(100vh-48px-85px)] md:h-[calc(100vh-70px)] overflow-hidden">
        <div className="flex-1 flex flex-col min-w-0 min-h-0">
          <Outlet context={{ hasConversations: false }} />
        </div>
      </div>
    )
  }

  if (isMobile) {
    if (isChatThread) {
      return (
        <div className="flex h-[calc(100vh-48px-85px)] overflow-hidden">
          <div className="flex-1 flex flex-col min-w-0 min-h-0">
            <Outlet context={{ hasConversations }} />
          </div>
        </div>
      )
    }
    return (
      <div className="flex h-[calc(100vh-48px-85px)] overflow-hidden">
        <ChatSidebar onCollapse={toggle} />
      </div>
    )
  }

  return (
    <div className="max-w-360 mx-auto flex h-[calc(100vh-70px)] overflow-hidden border-t min-[1445px]:border border-border min-[1445px]:rounded-[30px] mt-5">
      {collapsed ? (
        <CollapsedAvatarRail conversations={conversations} onExpand={toggle} />
      ) : (
        <ChatSidebar onCollapse={toggle} />
      )}
      <div className="flex-1 flex flex-col min-w-0 min-h-0">
        <Outlet context={{ hasConversations }} />
      </div>
    </div>
  )
}
